import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import * as React from 'react'
import { useIntl } from '@/intl'
import { PriceArea, PriceAreaSize } from '@/scopes/common/components/PriceArea'
import { AcmInformation, PriceDiscounts } from '@/scopes/search/search/infrastructure/api/types'
import { PriceDetails } from './PriceDetails'

export function PriceInfo({
  accommodationId,
  averagePrice,
  priceDiscounts,
  size = 'L',
  participants,
  acmInformation,
  ...rest
}: PriceInfoProps) {
  const intl = useIntl()
  const priceText = intl.formatMessage({ id: 'price_box_price_text' }, { isPerPerson: true })
  const priceFormatted = React.useMemo(() => intl.formatTruncatedCurrency(averagePrice), [averagePrice, intl])
  const formattedPriceDiscounts = React.useMemo(() => {
    if (!priceDiscounts || priceDiscounts.discountPercentage <= 0)
      return undefined

    const absoluteDiscountValue = priceDiscounts.originalAveragePrice - averagePrice

    return {
      discountLabel: `-${intl.formatCurrency(absoluteDiscountValue)}`,
      originalPrice: intl.formatCurrency(priceDiscounts.originalAveragePrice),
    }
  }, [priceDiscounts, intl, averagePrice])

  return (
    <PriceArea
      {...rest}
      price={priceFormatted}
      discounts={formattedPriceDiscounts}
      areaText={priceText}
      supportingText={priceDiscounts?.description}
      size={size}
    >
      {acmInformation && (
        <PriceDetails
          priceDetailsQueryParams={{
            transportType: acmInformation.transportType,
            accommodationId,
            duration: acmInformation.durationRaw,
            mealPlan: acmInformation.mealplanCode,
            departureDate: acmInformation.departureDateRaw,
            participants,
          }}
        />
      )}
    </PriceArea>
  )
}

export interface PriceInfoProps {
  accommodationId: number
  averagePrice: number
  priceDiscounts?: PriceDiscounts
  size?: PriceAreaSize
  participants?: RoomsParticipantDistribution
  acmInformation?: AcmInformation
}
